/**
 * FluorideIntelligenceCard — флуоридна интелигентност.
 * Общ дневен прием от всички източници, оптимален диапазон, риск от
 * флуороза и граници на острата токсичност спрямо теглото.
 * Данни: GET/POST /api/dental-prevention/fluoride
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Droplets, AlertTriangle, Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "@/App";

const STATUS = {
  low: { color: "#38BDF8", label: "под оптимума" },
  optimal: { color: "#10B981", label: "оптимален" },
  upper: { color: "#F59E0B", label: "над оптимума" },
  excess: { color: "#EF4444", label: "в излишък" },
  unknown: { color: "#94A3B8", label: "непълни данни" },
};
const FIELDS = [
  ["weight_kg", "Тегло (кг)", 0.5],
  ["age_years", "Възраст (г.)", 1],
  ["paste_ppm", "Паста (ppm F)", 50],
  ["paste_grams", "Паста (г на четкане)", 0.05],
  ["brush_times", "Четкания дневно", 1],
  ["water_ppm", "Вода (ppm F)", 0.1],
  ["water_liters", "Вода (л дневно)", 0.1],
  ["supplement_mg", "Добавка (mg F)", 0.25],
  ["rinse_ppm", "Вода за уста (ppm F)", 25],
  ["rinse_per_week", "Изплаквания седмично", 1],
];

export default function FluorideIntelligenceCard({ isDark = false, cardCls = "" }) {
  const { API } = useAuth();
  const [d, setD] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/dental-prevention/fluoride`);
      setD(data);
    } catch { setD(null); }
  }, [API]);

  useEffect(() => { load(); }, [load]);
  if (!d) return null;

  const border = isDark ? "border-white/10" : "border-gray-200";
  const inputCls = `px-2 py-1 rounded-lg border text-[12px] w-full ${
    isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"}`;
  const st = STATUS[d.status] || STATUS.unknown;
  const perKg = d.intake?.per_kg;
  const pct = perKg ? Math.min(100, (perKg / 0.14) * 100) : 0;

  const save = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.entries(form)
        .filter(([, v]) => v !== "" && v !== null && v !== undefined)
        .map(([k, v]) => [k, Number(v)]));
      const { data } = await axios.post(`${API}/dental-prevention/fluoride`, body);
      setD(data); setForm(null); toast.success("Флуоридният профил е обновен");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Не се записа");
    } finally { setBusy(false); }
  };

  return (
    <div className={cardCls || `p-4 rounded-2xl border ${border}`} data-testid="dhi-fluoride-card">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-bold inline-flex items-center gap-1.5">
            <Droplets className="w-4 h-4 text-cyan-500" /> Флуоридна интелигентност
          </div>
          <p className="text-[11px] opacity-70 mt-0.5">{d.intro_bg}</p>
        </div>
        <button onClick={() => setForm({ ...(d.profile || {}) })}
          className="px-2.5 py-1.5 rounded-lg text-[11px] font-bold text-white bg-gradient-to-r from-cyan-500 to-sky-600 shrink-0"
          data-testid="fluoride-edit">
          Изчисли приема
        </button>
      </div>

      {/* прием */}
      <div className={`mt-3 p-3 rounded-xl border ${border}`} data-testid="fluoride-intake">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-2xl font-extrabold" data-testid="fluoride-total">
            {d.intake?.total_mg ?? "—"} mg
          </span>
          <span className="text-[11px] opacity-70">флуорид дневно</span>
          {perKg != null && (
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: `${st.color}22`, color: st.color }}
              data-testid="fluoride-status">
              {perKg} mg/kg · {st.label}
            </span>
          )}
        </div>
        <div className="mt-2 h-2 rounded-full overflow-hidden"
          style={{ background: isDark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.06)" }}>
          <div className="h-full rounded-full transition-all"
            style={{ width: `${pct}%`, background: st.color }} />
        </div>
        <div className="flex justify-between text-[9px] opacity-55 mt-0.5">
          <span>0</span><span>оптимум 0.05-0.07</span><span>0.10 праг</span>
        </div>
        <p className="text-[11px] opacity-80 mt-2">{d.status_bg}</p>
        <div className="mt-2 space-y-1">
          {(d.intake?.sources || []).filter((s) => s.mg > 0).map((s) => (
            <div key={s.key} className="flex items-center justify-between gap-2 text-[10px]"
              data-testid={`fluoride-source-${s.key}`}>
              <span className="opacity-75 truncate">{s.label_bg}</span>
              <span className="font-bold shrink-0">{s.mg} mg</span>
            </div>
          ))}
        </div>
      </div>

      {/* формуляр */}
      {form && (
        <div className={`mt-3 p-3 rounded-xl border ${border}`} data-testid="fluoride-form">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {FIELDS.map(([k, label, step]) => (
              <div key={k}>
                <div className="text-[9px] uppercase font-bold opacity-55 mb-0.5">{label}</div>
                <input type="number" step={step} value={form[k] ?? ""}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className={inputCls} data-testid={`fluoride-${k}`} />
              </div>
            ))}
          </div>
          <div className="text-[9px] opacity-55 mt-1.5">{d.defaults_bg}</div>
          <div className="flex gap-2 mt-2">
            <button onClick={save} disabled={busy}
              className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-white bg-gradient-to-r from-cyan-500 to-sky-600 inline-flex items-center gap-1 disabled:opacity-60"
              data-testid="fluoride-save">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Запази
            </button>
            <button onClick={() => setForm(null)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-bold border ${border}`}
              data-testid="fluoride-cancel">Отказ</button>
          </div>
        </div>
      )}

      {/* флуороза */}
      {(d.fluorosis?.text_bg) && (
        <div className={`mt-3 p-2.5 rounded-xl border ${border}`} data-testid="fluoride-fluorosis">
          <div className="text-[11px] font-bold">
            Флуороза {d.fluorosis.risk ? `— риск: ${d.fluorosis.risk}` : ""}
          </div>
          <div className="text-[10px] opacity-75 mt-0.5">{d.fluorosis.text_bg}</div>
        </div>
      )}

      {/* остра токсичност */}
      {d.acute?.probably_toxic_mg && (
        <div className="mt-3 p-2.5 rounded-xl bg-rose-500/10 border border-rose-500/30"
          data-testid="fluoride-acute">
          <div className="text-[11px] font-bold text-rose-500 inline-flex items-center gap-1">
            <AlertTriangle className="w-3.5 h-3.5" /> Граница на безопасност при поглъщане
          </div>
          <div className="text-[10px] opacity-85 mt-1">{d.acute.text_bg}</div>
          <ul className="mt-1.5 space-y-0.5">
            {d.acute.first_aid_bg.map((s, i) => (
              <li key={i} className="text-[10px] opacity-80">· {s}</li>
            ))}
          </ul>
        </div>
      )}

      {/* протоколи */}
      <div className="mt-3 space-y-1.5" data-testid="fluoride-protocols">
        <div className="text-[10px] uppercase tracking-wider font-bold opacity-50">
          Топикални протоколи · категория риск: {d.risk_category}
        </div>
        {d.protocols.map((p) => (
          <div key={p.key}
            className={`p-2 rounded-lg border ${p.recommended ? "border-cyan-500/40 bg-cyan-500/5" : border}`}
            data-testid={`fluoride-protocol-${p.key}`}>
            <div className="text-[11px] font-bold inline-flex items-center gap-1">
              {p.recommended && <ShieldCheck className="w-3.5 h-3.5 text-cyan-500" />}
              {p.title_bg}
            </div>
            <div className="text-[10px] opacity-70 mt-0.5">За кого: {p.who_bg}</div>
            <div className="text-[10px] opacity-70">{p.how_bg}</div>
          </div>
        ))}
      </div>

      <div className="text-[9px] opacity-50 mt-2">{d.note_bg}</div>
    </div>
  );
}
